import type {
  AffineShape,
  ConstantFunctionShape,
  PiecewiseConstantShape,
  PiecewiseLinearShape,
  Transform,
} from '../../../types/adaptations'

/** Browser equivalent of the four backend shape evaluators, used for previews. */
export const evaluateTransform = (transform: Pick<Transform, 'shape_type' | 'shape'>, x: number): number => {
  switch (transform.shape_type) {
    case 'constant_function':
      return Number((transform.shape as ConstantFunctionShape).value)
    case 'affine': {
      const shape = transform.shape as AffineShape
      return Number(shape.slope) * x + Number(shape.intercept)
    }
    case 'piecewise_linear': {
      const shape = transform.shape as PiecewiseLinearShape
      const knots = shape.knots
      const values = shape.values
      if (x < knots[0]) {
        if (shape.bounds === 'error') return Number.NaN
        if (shape.bounds === 'extrapolate') {
          const slope = (values[1] - values[0]) / (knots[1] - knots[0])
          return values[0] + slope * (x - knots[0])
        }
        return values[0]
      }
      if (x > knots[knots.length - 1]) {
        if (shape.bounds === 'error') return Number.NaN
        if (shape.bounds === 'extrapolate') {
          const last = knots.length - 1
          const slope = (values[last] - values[last - 1]) / (knots[last] - knots[last - 1])
          return values[last] + slope * (x - knots[last])
        }
        return values[values.length - 1]
      }
      const upper = knots.findIndex(knot => knot >= x)
      if (upper <= 0) return values[0]
      if (knots[upper] === x) return values[upper]
      const lower = upper - 1
      const ratio = (x - knots[lower]) / (knots[upper] - knots[lower])
      return values[lower] + ratio * (values[upper] - values[lower])
    }
    case 'piecewise_constant': {
      const shape = transform.shape as PiecewiseConstantShape
      if (shape.side === 'left') {
        let index = 0
        for (let i = 0; i < shape.knots.length; i += 1) {
          if (shape.knots[i] <= x) index = i
          else break
        }
        return shape.values[index]
      }
      const index = shape.knots.findIndex(knot => knot >= x)
      return shape.values[index < 0 ? shape.values.length - 1 : index]
    }
    default:
      return Number.NaN
  }
}
